import type { ReactElement } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { mean } from 'mathjs';
import { CardBody } from 'reactstrap';
import { Accordion } from 'atom-components/accordion/accordion';
import YearLevel from 'feature-components/display/year-level';
import ChartOverViewTable from 'feature-components/report-parts/chart-overview-table/chart-overview-table';
import ReportFooter from 'feature-components/report-parts/report-footer';
import CategoryScoreChart from 'feature-components/reports/category-report/category-score-chart/category-score-chart';
import UsersTable from 'pages/test-instances/components/users/users-table/users-table';
import type { BaseReferenceDataCategoryNational } from 'services/assist-api/entities/reference-data-category-national/reference-datas-category-nationals';
import type {
  ReportTestInstanceUser,
  ResponseCategory
} from 'services/assist-api/entities/test-instance-response/reports/report-responses';
import type { TestCategory } from 'services/assist-api/entities/test-item/test-items';
import './category-card.scss';

type Props = {
  category: TestCategory;
  usersWithResponse: {
    user?: Partial<
      Pick<
        ReportTestInstanceUser,
        | 'identifier'
        | 'firstname'
        | 'lastname'
        | 'level'
        | 'referenceLevel'
        | 'gender'
        | 'ethnicities'
        | 'groups'
      >
    > &
      Pick<ReportTestInstanceUser, 'id'>;
    response?: ResponseCategory;
  }[];
  totalStudents: number;
  referenceLevelPair?: { level: number; referenceLevel: number };
  testName: string;
  startDate: string;
  endDate: string;
  theme?: string;
  categoryNationalNorms?: BaseReferenceDataCategoryNational;
};

export default function CategoryCard({
  category,
  usersWithResponse,
  totalStudents,
  referenceLevelPair,
  testName,
  categoryNationalNorms,
  startDate,
  endDate,
  theme
}: Props): ReactElement {
  const [selectedScore, setSelectedScore] = useState<number | null>();

  // if our input data changes reset selected
  useEffect(() => {
    setSelectedScore(null);
  }, [usersWithResponse]);

  // these need to be memo to stop the chart redrawing
  const numQuestions = useMemo(() => category.questionIds.length, [category.questionIds.length]);
  const classMean = useMemo(
    () =>
      usersWithResponse?.length
        ? mean(usersWithResponse.map((user) => user.response?.rawScore || 0))
        : 0,
    [usersWithResponse]
  );
  const means = useMemo(
    () => ({ class: classMean, reference: categoryNationalNorms?.rawMean }),
    [categoryNationalNorms?.rawMean, classMean]
  );

  const selectedUsers = useMemo(
    () =>
      selectedScore !== null
        ? usersWithResponse.filter((user) => user.response?.rawScore === selectedScore)
        : [],
    [selectedScore, usersWithResponse]
  );

  return (
    <Accordion header={`${testName}: ${category.name}`} className="category-report-card">
      <CardBody>
        <ChartOverViewTable
          theme={theme}
          data={[
            {
              label: 'Subtest',
              value: category.name
            },
            {
              label: 'Number of Students (Completed)',
              value: `${usersWithResponse.length} / ${totalStudents}`
            },
            {
              label: 'Mean Score',
              value: `${means.class.toFixed(1)} ${
                means.reference && referenceLevelPair ? `(${means.reference.toFixed(1)})` : ''
              }`
            },
            {
              label: 'Year level (ref year)',
              value: referenceLevelPair ? (
                <>
                  <YearLevel levelId={referenceLevelPair?.level} /> (
                  <YearLevel levelId={referenceLevelPair?.referenceLevel} />)
                </>
              ) : (
                <i>Select a year level to see national reference data</i>
              )
            }
          ]}
        />
        <CategoryScoreChart
          responses={usersWithResponse}
          numberOfQuestions={numQuestions}
          mean={means}
          criticalScore={categoryNationalNorms?.rawCrit}
          typicalRange={{
            min: categoryNationalNorms?.rawLower,
            max: categoryNationalNorms?.rawUpper
          }}
          onSelectScore={setSelectedScore}
        />
        {!!selectedUsers?.length && (
          <div className="mx-4">
            <hr className="mx-4" />
            <h4>
              Learners with Score: {selectedScore}/{numQuestions}
            </h4>
            <UsersTable
              users={selectedUsers.map(({ user }) => ({
                ...user,
                firstName: user?.firstname,
                lastName: user?.lastname,
                levelId: user?.level.id,
                genderId: user?.gender.id,
                groupIds: user?.groups?.map(({ id }) => id),
                ethnicityIds: user?.ethnicities?.map(({ id }) => id),
                id: user?.id
              }))}
              hideFilters={selectedUsers?.length < 15}
              isAssessmentUser={{ hideTokenColumn: true, hideStatusColumn: true }}
            />
          </div>
        )}
        <ReportFooter theme={theme} period={{ startDate, endDate }} />
      </CardBody>
    </Accordion>
  );
}
